import moment from "moment";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { TableColumn } from "react-data-table-component";
import {
  Badge,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Input,
  Label,
  Button,
  Nav,
  NavItem,
  NavLink,
  Spinner,
} from "reactstrap";

import {
  AppTable,
  ColumnHeader,
  TableSpinner,
  TextColumn,
} from "../../components";
import { BreadCrumb } from "../../components/Common";
import {
  GetLiveLectureStatsQuery,
  GetLiveLecturesInRangeRequest,
  LiveLectureDto,
  PaginateQuery,
} from "../../interfaces";
import { EducationalStageDto } from "../../interfaces/curriculum";
import { SelectListDto } from "../../models";
import { getStartAndEndOfCurrentMonth } from "../../services";
import {
  useGetLiveLectureStatsQuery,
  useGetLiveLecturesQuery,
  useLazyGetLiveLecturesInRangeQuery,
  useLazyGetSessionMeetingLinkQuery,
} from "../../store/api/liveLectureApi";
import { useGetEducationSystemSelectListQuery } from "../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { useGetEducationalStagesQuery } from "../../store/api/educationalStageApi";
import { useGetSubjectsSelectListByGradeQuery } from "../../store/api/subjectApi";
import {
  LiveLectureAccessModeEnum,
  LiveLectureStatusEnum,
  RoleEnum,
} from "../../enums";
import { AccountState } from "../../store/slices/accountSlice";
import {
  LIVE_LECTURE_LEGEND_STATUSES,
  canJoinLiveLecture,
  formatLiveLectureDate,
  formatLiveLectureTimeSlot,
  getLiveLectureEventColor,
  isPastLiveLecture,
} from "./liveLectureCalendarUtils";
import {
  lectureRoomId,
  LiveLecturePresenceLine,
  useLiveLectureClock,
  useLiveLecturePresence,
} from "./useLiveLecturePresence";
import { openLiveCall } from "./openLiveCall";
import { LiveLectureStatusDot } from "./LiveLectureStatusDot";
import { LiveLectureCalendarEvent } from "./LiveLectureCalendarEvent";
import { LiveLectureStats } from "./LiveLectureStats";
import { LiveLectureDetailModal } from "./LiveLectureDetailModal";
import { AddEditLiveLectureModal } from "./AddEditLiveLectureModal";
import { AddEditPrivateLiveLectureModal } from "./AddEditPrivateLiveLectureModal";
import { LiveLectureRecordingsModal } from "./LiveLectureRecordings";
import {
  isRecordingPending,
  lectureRecordings,
} from "./recordingMedia";

const localizer = momentLocalizer(moment);

type CalendarRange = {
  startAt: Date;
  endAt: Date;
};

type LecturePeriodTab = "ongoing" | "upcoming" | "previous";

const joinableStatuses = [
  LiveLectureStatusEnum.PUBLISHED,
  LiveLectureStatusEnum.FULL,
  LiveLectureStatusEnum.IN_PROGRESS,
];

const labelOf = (
  item:
    | {
        value?: { en?: string; ar?: string };
        name?: { en?: string; ar?: string };
      }
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

export const LiveLecturePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const langKey = i18n.language === "ar" ? "ar" : "en";
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  const roleKey = profile?.role?.key;
  const isSuperAdmin = roleKey === RoleEnum.SUPER_ADMIN;
  const isHeadTeacherAdmin = roleKey === RoleEnum.HEAD_TEACHER_ADMIN;
  const isSystemAdmin = roleKey === RoleEnum.SYSTEM_ADMIN;
  const isTeacherAdmin = roleKey === RoleEnum.TEACHER_ADMIN;
  const isAdmin = roleKey === RoleEnum.ADMIN;

  const [events, setEvents] = useState<any[]>([]);
  const [lecturePeriod, setLecturePeriod] =
    useState<LecturePeriodTab>("ongoing");
  const [selectedLectureId, setSelectedLectureId] = useState<string | null>(
    null,
  );
  const [editingLectureId, setEditingLectureId] = useState<string | null>(null);
  const [editingAccessMode, setEditingAccessMode] =
    useState<LiveLectureAccessModeEnum | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [range, setRange] = useState<CalendarRange>();

  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [stageId, setStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [ongoingQuery, setOngoingQuery] = useState(
    () => new PaginateQuery(10, "start_at:ASC", { period: "ongoing" }),
  );
  const [previousQuery, setPreviousQuery] = useState(
    () => new PaginateQuery(10, "start_at:DESC", { period: "previous" }),
  );

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !educationSystemId },
  );
  const { data: allCourses } = useGetCourseSelectListQuery();
  const courses = useMemo(() => {
    if (isTeacherAdmin && profile?.courses?.length) {
      const assignedIds = new Set(profile.courses.map((c) => c.id));
      return (allCourses || []).filter((c: any) => assignedIds.has(c.id));
    }
    return allCourses || [];
  }, [allCourses, isTeacherAdmin, profile?.courses]);

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = { curriculumId, courseId };
    return q;
  }, [curriculumId, courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !curriculumId || !courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];
  const selectedStage = useMemo(
    () => stages.find((s) => s.id === stageId),
    [stages, stageId],
  );
  const grades = selectedStage?.grades || [];

  const { data: gradeSubjects = [] } = useGetSubjectsSelectListByGradeQuery(
    gradeId,
    { skip: !gradeId },
  );

  const hierarchyFilters = useMemo<GetLiveLectureStatsQuery>(
    () => ({
      ...(educationSystemId ? { educationSystemId } : {}),
      ...(curriculumId ? { curriculumId } : {}),
      ...(courseId ? { courseId } : {}),
      ...(stageId ? { stageId } : {}),
      ...(gradeId ? { gradeId } : {}),
      ...(subjectId ? { subjectId } : {}),
    }),
    [
      educationSystemId,
      curriculumId,
      courseId,
      stageId,
      gradeId,
      subjectId,
    ],
  );

  const [getLiveLecturesInRange, { data: lecturesResponse }] =
    useLazyGetLiveLecturesInRangeQuery();
  const { data: stats, isLoading: isLoadingStats } =
    useGetLiveLectureStatsQuery();
  const [recordingPollInterval, setRecordingPollInterval] = useState(0);
  const [recordingLecture, setRecordingLecture] =
    useState<LiveLectureDto | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [getSessionMeetingLink] = useLazyGetSessionMeetingLinkQuery();
  const {
    data: ongoingResponse,
    isLoading: isLoadingOngoing,
    isFetching: isFetchingOngoing,
  } = useGetLiveLecturesQuery(ongoingQuery, {
    pollingInterval: recordingPollInterval,
  });
  const {
    data: previousResponse,
    isLoading: isLoadingPrevious,
    isFetching: isFetchingPrevious,
  } = useGetLiveLecturesQuery(previousQuery, {
    pollingInterval: recordingPollInterval,
  });

  document.title = `${t("documentTitle", { pageName: "liveLectures" })}`;

  const hasHierarchyFilters = Object.keys(hierarchyFilters).length > 0;

  const assignedEducationSystems = useMemo(() => {
    if (!isHeadTeacherAdmin && !isSystemAdmin) return [];
    return educationSystems || [];
  }, [isHeadTeacherAdmin, isSystemAdmin, educationSystems]);

  const assignedCourses = useMemo(() => {
    if (!isTeacherAdmin) return [];
    return profile?.courses || [];
  }, [isTeacherAdmin, profile?.courses]);

  const toRangeRequest = useCallback(
    (calendarRange: CalendarRange): GetLiveLecturesInRangeRequest => ({
      startAt: calendarRange.startAt.toISOString(),
      endAt: calendarRange.endAt.toISOString(),
      ...hierarchyFilters,
    }),
    [hierarchyFilters],
  );

  useEffect(() => {
    const { startOfMonth, endOfMonth } = getStartAndEndOfCurrentMonth();
    setRange({
      startAt: startOfMonth,
      endAt: endOfMonth,
    });
  }, []);

  useEffect(() => {
    if (range?.startAt && range?.endAt) {
      getLiveLecturesInRange(toRangeRequest(range));
    }
  }, [range, getLiveLecturesInRange, toRangeRequest]);

  useEffect(() => {
    if (lecturesResponse) {
      const lectures = Array.isArray(lecturesResponse) ? lecturesResponse : [];
      setEvents(
        lectures.map((lecture: LiveLectureDto) => {
          const title =
            i18n.language === "ar" ? lecture.title.ar : lecture.title.en;

          return {
            id: lecture.id,
            title: `${title}`,
            end: new Date(lecture.endAt),
            start: new Date(lecture.startAt),
            resource: lecture,
          };
        }),
      );
    }
  }, [lecturesResponse, i18n.language]);

  const upcomingEvents = useMemo(
    () => events.filter((event) => new Date(event.start).getTime() > Date.now()),
    [events],
  );

  const ongoingLectures = ongoingResponse?.data || [];
  const previousLectures = previousResponse?.data || [];
  const now = useLiveLectureClock();
  const watchLectures = useMemo(
    () => [...ongoingLectures, ...previousLectures],
    [ongoingLectures, previousLectures],
  );
  const presenceByRoom = useLiveLecturePresence(watchLectures, now);

  useEffect(() => {
    const pending = [...ongoingLectures, ...previousLectures].some((lecture) =>
      lectureRecordings(lecture.media).some(isRecordingPending),
    );
    setRecordingPollInterval(pending ? 15000 : 0);
  }, [ongoingLectures, previousLectures]);

  useEffect(() => {
    const syncPeriodQuery = (
      current: PaginateQuery,
      period: "ongoing" | "previous",
    ) => {
      const filter = { period, ...hierarchyFilters };
      if (
        current.page === 1 &&
        JSON.stringify(current.filter) === JSON.stringify(filter)
      ) {
        return current;
      }
      return { ...current, page: 1, filter };
    };

    setOngoingQuery((current) => syncPeriodQuery(current, "ongoing"));
    setPreviousQuery((current) => syncPeriodQuery(current, "previous"));
  }, [hierarchyFilters]);

  const clearHierarchyFilters = () => {
    setEducationSystemId("");
    setCurriculumId("");
    setCourseId("");
    setStageId("");
    setGradeId("");
    setSubjectId("");
  };

  const handleSelectEvent = (event: any) => {
    setSelectedLectureId(event.id);
  };

  const openLectureDetail = useCallback((lecture: LiveLectureDto) => {
    setSelectedLectureId(lecture.id);
  }, []);

  const onJoin = useCallback(
    async (row: LiveLectureDto) => {
      setJoiningId(row.id);
      try {
        const sessionLink = await getSessionMeetingLink(row.id).unwrap();
        if (sessionLink.canJoin && sessionLink.token) {
          const joined = await openLiveCall(sessionLink, history, {
            title: row.title?.[langKey],
            returnPath: history.location.pathname,
          });
          if (!joined) {
            toast.error(t("liveCallMicPermissionDenied"));
          }
        } else {
          toast.error(t("joinLiveLectureNotAvailable"));
        }
      } catch {
        toast.error(t("joinLiveLectureError"));
      } finally {
        setJoiningId(null);
      }
    },
    [getSessionMeetingLink, history, langKey, t],
  );

  const handleEditLecture = () => {
    if (!selectedLectureId) return;
    const selected = events.find((event) => event.id === selectedLectureId);
    const fromTable = [...ongoingLectures, ...previousLectures].find(
      (lecture) => lecture.id === selectedLectureId,
    );
    const accessMode =
      (selected?.resource as LiveLectureDto | undefined)?.accessMode ??
      fromTable?.accessMode ??
      LiveLectureAccessModeEnum.SUBJECT_INCLUDED;
    setEditingAccessMode(accessMode);
    setEditingLectureId(selectedLectureId);
    setSelectedLectureId(null);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingLectureId(null);
    setEditingAccessMode(null);
    if (range?.startAt && range?.endAt) {
      getLiveLecturesInRange(toRangeRequest(range));
    }
  };

  const fetchEventsForRange = useCallback(
    (calendarRange: Date[] | { start: Date; end: Date }) => {
      if (Array.isArray(calendarRange)) {
        const from = new Date(calendarRange[0]);
        const to = new Date(calendarRange[calendarRange.length - 1]);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        setRange({
          startAt: from,
          endAt: to,
        });
      } else {
        setRange({
          startAt: calendarRange.start,
          endAt: calendarRange.end,
        });
      }
    },
    [],
  );

  const buildLectureColumns = useCallback(() => {
    const columns: TableColumn<LiveLectureDto>[] = [
      {
        id: "title",
        name: <ColumnHeader title="title" />,
        cell: (row) => (
          <TextColumn text={row.title[langKey]} isNeedToTranslate={false} />
        ),
        sortable: true,
      },
    ];

    if (!isTeacherAdmin) {
      columns.push({
        name: <ColumnHeader title="teacher" />,
        cell: (row) => (
          <TextColumn
            text={row.teacher?.name?.[langKey] || "-"}
            isNeedToTranslate={false}
          />
        ),
        sortable: false,
      });
    }

    columns.push(
      {
        name: <ColumnHeader title="subject" />,
        cell: (row) => (
          <TextColumn
            text={row.subject?.name?.[langKey] || "-"}
            isNeedToTranslate={false}
          />
        ),
        sortable: false,
      },
      {
        name: <ColumnHeader title="liveLectureAccessMode" />,
        cell: (row) => (
          <TextColumn text={t(`liveLectureAccessMode_${row.accessMode}`)} />
        ),
        sortable: false,
      },
      {
        id: "start_at",
        name: <ColumnHeader title="time" />,
        cell: (row) => (
          <div>
            <div>{formatLiveLectureDate(row.startAt)}</div>
            <div className="text-muted small">
              {formatLiveLectureTimeSlot(row.startAt, row.endAt)}
            </div>
          </div>
        ),
        sortable: true,
      },
      {
        name: <ColumnHeader title="duration" />,
        cell: (row) => (
          <TextColumn
            text={`${row.durationMinutes} ${t("minutes")}`}
            isNeedToTranslate={false}
          />
        ),
        sortable: false,
      },
      {
        name: <ColumnHeader title="status" />,
        cell: (row) => (
          <span className="d-inline-flex align-items-center gap-1">
            <LiveLectureStatusDot status={row.status} size={8} />
            <TextColumn text={t(`liveLectureStatus_${row.status}`)} />
          </span>
        ),
        sortable: false,
      },
      {
        name: <ColumnHeader title="liveLectureCall" />,
        minWidth: "180px",
        cell: (row) => (
          <LiveLecturePresenceLine
            startAt={row.startAt}
            endAt={row.endAt}
            now={now}
            presence={presenceByRoom[lectureRoomId(row)]}
          />
        ),
        sortable: false,
      },
      {
        name: <ColumnHeader title="action" />,
        sortable: false,
        minWidth: "340px",
        grow: 0,
        cell: (row) => {
          const recordings = lectureRecordings(row.media);
          const recordingPending = recordings.some(isRecordingPending);
          const recordingLabel = recordingPending
            ? t("liveLectureRecordingInProgress", {
                defaultValue: "In progress",
              })
            : t("liveLectureRecordings", { defaultValue: "Recordings" });

          return (
            <div className="d-flex align-items-center gap-1">
              {canJoinLiveLecture(profile) &&
              joinableStatuses.includes(row.status) &&
              !isPastLiveLecture(row.endAt, now) ? (
                <Button
                  color="primary"
                  size="sm"
                  title={t("joinLiveLecture")}
                  disabled={joiningId === row.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    onJoin(row);
                  }}
                >
                  {joiningId === row.id ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="ri-live-line me-1"></i>
                  )}
                  {t("joinLiveLecture")}
                </Button>
              ) : null}
              <Button
                color="soft-success"
                size="sm"
                title={t("details")}
                onClick={(event) => {
                  event.stopPropagation();
                  openLectureDetail(row);
                }}
              >
                <i className="ri-file-list-3-line me-1"></i>
                {t("details")}
              </Button>
              {recordings.length > 0 ? (
                <Button
                  color="primary"
                  size="sm"
                  title={recordingLabel}
                  onClick={(event) => {
                    event.stopPropagation();
                    setRecordingLecture(row);
                  }}
                >
                  <i
                    className={
                      recordingPending
                        ? "ri-time-line me-1"
                        : "ri-play-circle-line me-1"
                    }
                  ></i>
                  {recordingLabel}
                </Button>
              ) : null}
            </div>
          );
        },
      },
    );

    return columns;
  }, [
    isTeacherAdmin,
    joiningId,
    langKey,
    now,
    onJoin,
    openLectureDetail,
    presenceByRoom,
    profile,
    t,
  ]);

  const ongoingColumns = useMemo(
    () => buildLectureColumns(),
    [buildLectureColumns],
  );
  const previousColumns = useMemo(
    () => buildLectureColumns(),
    [buildLectureColumns],
  );

  const onListPageChange =
    (update: typeof setPreviousQuery) => (page: number) => {
      update((current) => ({ ...current, page }));
    };

  const onListRowsPerPageChange =
    (update: typeof setPreviousQuery) =>
    (currentRowsPerPage: number, currentPage: number) => {
      update((current) => ({
        ...current,
        limit: currentRowsPerPage,
        page: currentPage,
      }));
    };

  const onListSort =
    (update: typeof setPreviousQuery) =>
    (column: TableColumn<LiveLectureDto>, sortDirection: string) => {
      const direction = sortDirection.toUpperCase();
      const sortBy =
        column.id === "title"
          ? `title_${langKey}:${direction}`
          : `start_at:${direction}`;
      update((current) => ({ ...current, sortBy }));
    };

  const eventPropGetter = useCallback((event: any) => {
    const lecture = event.resource as LiveLectureDto | undefined;
    if (!lecture) {
      return {};
    }

    const colors = getLiveLectureEventColor(lecture.status, lecture.accessMode);
    return {
      style: {
        ...colors,
        color: "#fff",
        borderWidth: 1,
        borderStyle:
          lecture.accessMode === LiveLectureAccessModeEnum.SUBJECT_INCLUDED
            ? "dashed"
            : "solid",
        fontSize: "0.78rem",
      },
    };
  }, []);

  const scopeHint = isHeadTeacherAdmin || isSystemAdmin
    ? t("liveLecturesAssignedEducationSystemsHint", {
        defaultValue:
          "Showing live lectures under your assigned education systems.",
      })
    : isTeacherAdmin
      ? t("liveLecturesAssignedSubjectsHint", {
          defaultValue: "Showing live lectures for your assigned subjects.",
        })
      : isAdmin
        ? t("liveLecturesAllHint", {
            defaultValue: "All subject-included and paid live lectures.",
          })
        : t("liveLecturesAllHint");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("liveLectures")} pageTitle="dashboard" />

        <LiveLectureStats stats={stats} isLoading={isLoadingStats} />

        <Nav tabs className="nav-tabs-custom mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: "pointer" }}
              className={classnames({
                active: lecturePeriod === "ongoing",
              })}
              onClick={() => setLecturePeriod("ongoing")}
            >
              <i className="ri-live-line align-bottom me-1"></i>
              {t("ongoingLiveLectures", { defaultValue: "Ongoing" })}
              <Badge color="danger" pill className="ms-2">
                {ongoingResponse?.meta?.totalItems ?? 0}
              </Badge>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: "pointer" }}
              className={classnames({
                active: lecturePeriod === "upcoming",
              })}
              onClick={() => setLecturePeriod("upcoming")}
            >
              <i className="ri-calendar-event-line align-bottom me-1"></i>
              {t("upcomingLiveLectures", { defaultValue: "Upcoming" })}
              <Badge color="success" pill className="ms-2">
                {upcomingEvents.length}
              </Badge>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: "pointer" }}
              className={classnames({
                active: lecturePeriod === "previous",
              })}
              onClick={() => setLecturePeriod("previous")}
            >
              <i className="ri-history-line align-bottom me-1"></i>
              {t("previousLiveLectures", { defaultValue: "Previous" })}
              <Badge color="secondary" pill className="ms-2">
                {previousResponse?.meta?.totalItems ?? 0}
              </Badge>
            </NavLink>
          </NavItem>
        </Nav>

        {((isHeadTeacherAdmin || isSystemAdmin) && assignedEducationSystems.length > 0) ||
        (isTeacherAdmin && assignedCourses.length > 0) ? (
          <Card className="mb-3">
            <CardBody className="py-3">
              {isHeadTeacherAdmin || isSystemAdmin ? (
                <>
                  <div className="fw-medium mb-2">
                    {t("assignedEducationSystems", {
                      defaultValue: "Assigned education systems",
                    })}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {assignedEducationSystems.map((system: SelectListDto) => (
                      <Badge
                        key={system.id}
                        color="primary"
                        className="fs-12 fw-normal"
                        pill
                      >
                        {labelOf(system, langKey)}
                      </Badge>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="fw-medium mb-2">
                    {t("assignedCourses", {
                      defaultValue: "Assigned courses",
                    })}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {assignedCourses.map((course: SelectListDto) => (
                      <Badge
                        key={course.id}
                        color="info"
                        className="fs-12 fw-normal"
                        pill
                      >
                        {labelOf(course, langKey)}
                      </Badge>
                    ))}
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        ) : null}

        <Card className="mb-3">
          <CardBody>
            <div className="fw-medium mb-2">{t("filters", { defaultValue: "Filters" })}</div>
            <Row className="g-3">
              <Col xs={12} sm={6} lg={4}>
                <Label className="form-label mb-1">
                  {t("educationSystem")}
                </Label>
                <Input
                  type="select"
                  value={educationSystemId}
                  onChange={(e) => {
                    setEducationSystemId(e.target.value);
                    setCurriculumId("");
                    setStageId("");
                    setGradeId("");
                    setSubjectId("");
                  }}
                >
                  <option value="">{t("all")}</option>
                  {(educationSystems || []).map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {labelOf(s, langKey)}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Label className="form-label mb-1">
                  {t("curriculumItem")}
                </Label>
                <Input
                  type="select"
                  value={curriculumId}
                  disabled={!educationSystemId}
                  onChange={(e) => {
                    setCurriculumId(e.target.value);
                    setStageId("");
                    setGradeId("");
                    setSubjectId("");
                  }}
                >
                  <option value="">{t("all")}</option>
                  {(curricula || []).map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {labelOf(c, langKey)}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Label className="form-label mb-1">{t("course")}</Label>
                <Input
                  type="select"
                  value={courseId}
                  onChange={(e) => {
                    setCourseId(e.target.value);
                    setStageId("");
                    setGradeId("");
                    setSubjectId("");
                  }}
                >
                  <option value="">{t("all")}</option>
                  {(courses || []).map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {labelOf(c, langKey)}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Label className="form-label mb-1">
                  {t("educationalStage")}
                </Label>
                <Input
                  type="select"
                  value={stageId}
                  disabled={!curriculumId || !courseId}
                  onChange={(e) => {
                    setStageId(e.target.value);
                    setGradeId("");
                    setSubjectId("");
                  }}
                >
                  <option value="">{t("all")}</option>
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name[langKey] || stage.name.en}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Label className="form-label mb-1">{t("grade")}</Label>
                <Input
                  type="select"
                  value={gradeId}
                  disabled={!stageId}
                  onChange={(e) => {
                    setGradeId(e.target.value);
                    setSubjectId("");
                  }}
                >
                  <option value="">{t("all")}</option>
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name[langKey] || grade.name.en}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Label className="form-label mb-1">{t("subject")}</Label>
                <Input
                  type="select"
                  value={subjectId}
                  disabled={!gradeId}
                  onChange={(e) => setSubjectId(e.target.value)}
                >
                  <option value="">{t("all")}</option>
                  {gradeSubjects.map((s: SelectListDto) => (
                    <option key={s.id} value={s.id}>
                      {labelOf(s, langKey)}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>
            {hasHierarchyFilters ? (
              <div className="mt-2 text-end">
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                  onClick={clearHierarchyFilters}
                >
                  <i className="ri-close-circle-line me-1"></i>
                  {t("clearFilters")}
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Row>
          <Col>
            <Card>
              <CardHeader>
                <h6 className="card-title mb-0">{t("liveLectures")}</h6>
                <p className="text-muted small mb-0 mt-1">{scopeHint}</p>
              </CardHeader>
              <CardBody>
                {lecturePeriod === "upcoming" ? (
                <React.Fragment>
                <div className="d-flex flex-wrap gap-3 mb-3 small text-muted">
                  {LIVE_LECTURE_LEGEND_STATUSES.map((status) => (
                    <span
                      key={status}
                      className="d-inline-flex align-items-center gap-1"
                    >
                      <LiveLectureStatusDot status={status} size={10} />
                      {t(`liveLectureStatus_${status}`)}
                    </span>
                  ))}
                  <span className="d-inline-flex align-items-center gap-1">
                    <span
                      className="d-inline-block rounded"
                      style={{
                        width: 18,
                        height: 10,
                        border: "1px solid #50a5f1",
                        borderStyle: "dashed",
                      }}
                    />
                    {t("liveLectureAccessMode_SUBJECT_INCLUDED")}
                  </span>
                  <span className="d-inline-flex align-items-center gap-1">
                    <span
                      className="d-inline-block rounded"
                      style={{
                        width: 18,
                        height: 10,
                        border: "1px solid #364574",
                      }}
                    />
                    {t("liveLectureAccessMode_STANDALONE_PAID")}
                  </span>
                  {isSuperAdmin ? (
                    <span className="d-inline-flex align-items-center gap-1">
                      <span
                        className="d-inline-block rounded"
                        style={{
                          width: 18,
                          height: 10,
                          background: "#212529",
                        }}
                      />
                      {t("liveLectureAccessMode_PRIVATE")}
                    </span>
                  ) : null}
                </div>
                <div className="vh-100 mt-4">
                  <Calendar
                    culture={i18n.language === "ar" ? "ar-AE" : "en"}
                    localizer={localizer}
                    events={upcomingEvents}
                    startAccessor="start"
                    endAccessor="end"
                    components={{ event: LiveLectureCalendarEvent }}
                    eventPropGetter={eventPropGetter}
                    onSelectEvent={handleSelectEvent}
                    onRangeChange={fetchEventsForRange}
                    selectable={false}
                    messages={
                      i18n.language === "ar"
                        ? {
                            week: t("week"),
                            work_week: t("workWeek"),
                            day: t("day"),
                            month: t("month"),
                            previous: t("previous"),
                            next: t("next"),
                            today: t("today"),
                            agenda: t("event"),
                            allDay: t("allDay"),
                            noEventsInRange: t("noEventsInRange"),
                            date: t("date"),
                            event: t("event"),
                            time: t("time"),
                            tomorrow: t("tomorrow"),
                            yesterday: t("yesterday"),
                            showMore: (total) => `+${total} ${t("total")}`,
                          }
                        : {}
                    }
                    rtl={i18n.language === "ar"}
                    popup
                  />
                </div>
                </React.Fragment>
                ) : lecturePeriod === "previous" ? (
                  <AppTable
                    key={JSON.stringify(previousQuery.filter)}
                    columns={previousColumns}
                    data={previousLectures}
                    progressPending={isLoadingPrevious || isFetchingPrevious}
                    progressComponent={<TableSpinner />}
                    onRowClicked={openLectureDetail}
                    onSort={onListSort(setPreviousQuery)}
                    onChangeRowsPerPage={onListRowsPerPageChange(setPreviousQuery)}
                    onChangePage={onListPageChange(setPreviousQuery)}
                    paginationTotalRows={previousResponse?.meta?.totalItems}
                    paginationDefaultPage={previousQuery.page}
                    paginationPerPage={previousQuery.limit}
                    defaultSortFieldId="start_at"
                    defaultSortAsc={false}
                  />
                ) : (
                  <AppTable
                    key={JSON.stringify(ongoingQuery.filter)}
                    columns={ongoingColumns}
                    data={ongoingLectures}
                    progressPending={isLoadingOngoing || isFetchingOngoing}
                    progressComponent={<TableSpinner />}
                    onRowClicked={openLectureDetail}
                    onSort={onListSort(setOngoingQuery)}
                    onChangeRowsPerPage={onListRowsPerPageChange(setOngoingQuery)}
                    onChangePage={onListPageChange(setOngoingQuery)}
                    paginationTotalRows={ongoingResponse?.meta?.totalItems}
                    paginationDefaultPage={ongoingQuery.page}
                    paginationPerPage={ongoingQuery.limit}
                    defaultSortFieldId="start_at"
                    defaultSortAsc
                  />
                )}
                <LiveLectureDetailModal
                  liveLectureId={selectedLectureId}
                  show={Boolean(selectedLectureId)}
                  onCloseClick={() => setSelectedLectureId(null)}
                  profile={profile ?? undefined}
                  onEditClick={handleEditLecture}
                />
                <AddEditLiveLectureModal
                  key={
                    editingAccessMode === LiveLectureAccessModeEnum.PRIVATE
                      ? "calendar-edit-skip"
                      : editingLectureId || "calendar-edit"
                  }
                  id={
                    editingAccessMode === LiveLectureAccessModeEnum.PRIVATE
                      ? null
                      : editingLectureId
                  }
                  show={
                    showEditModal &&
                    editingAccessMode !== LiveLectureAccessModeEnum.PRIVATE
                  }
                  onCloseClick={handleCloseEditModal}
                  accessMode={LiveLectureAccessModeEnum.SUBJECT_INCLUDED}
                />
                {isSuperAdmin ? (
                  <AddEditPrivateLiveLectureModal
                    key={
                      editingAccessMode === LiveLectureAccessModeEnum.PRIVATE
                        ? editingLectureId || "private-edit"
                        : "private-edit-closed"
                    }
                    id={
                      editingAccessMode === LiveLectureAccessModeEnum.PRIVATE
                        ? editingLectureId
                        : null
                    }
                    show={
                      showEditModal &&
                      editingAccessMode === LiveLectureAccessModeEnum.PRIVATE
                    }
                    onCloseClick={handleCloseEditModal}
                  />
                ) : null}
                <LiveLectureRecordingsModal
                  lecture={
                    recordingLecture
                      ? [...ongoingLectures, ...previousLectures].find(
                          (item) => item.id === recordingLecture.id,
                        ) || recordingLecture
                      : null
                  }
                  show={Boolean(recordingLecture)}
                  langKey={langKey}
                  onClose={() => setRecordingLecture(null)}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
